import { vec3 } from 'gl-matrix';
import { BoundingBox, BoundingBoxObject } from '../util';
import { Ray } from '../math';

export const UNASSIGNED_REGION_ID = -1;

export class PathNode implements BoundingBoxObject {
  private bounds: BoundingBox;
  public id: number = 0;
  public neighbours: PathNode[];
  public reference: string;
  public regionId: number = UNASSIGNED_REGION_ID;

  constructor(
    public min: vec3 | number[],
    public max: vec3 | number[],
  ) {
    this.bounds = new BoundingBox(min, max);
    this.neighbours = [];
    this.reference = '';
  }

  public addNeighbour(neighbour: PathNode) {
    if (this.neighbours.indexOf(neighbour) == -1) {
      this.neighbours.push(neighbour);
    }
    if (neighbour.neighbours.indexOf(this) == -1) {
      neighbour.neighbours.push(this);
    }
  }

  public get position(): vec3 {
    return this.bounds.center;
  }

  getPosition(): vec3 {
    return this.position;
  }

  getBoundingBox(): BoundingBox {
    return this.bounds;
  }

  canIntersectRay(): boolean {
    return true;
  }

  intersectsRay(ray: Ray): boolean {
    return this.bounds.intersectsRay(ray);
  }

  getRayIntersection(ray: Ray, testBorders: boolean): vec3 | undefined {
    let faces = testBorders ? this.generateBox() : this.generateFloorQuad();

    for (let face of faces) {
      let intersection = ray.getPlaneIntersection(face.vertexA, face.vertexB, face.vertexC);
      if (intersection) {
        return intersection;
      }
    }

    return undefined;
  }

  private generateFloorQuad(): FaceLike[] {
    let min = this.min;
    let max = this.max;
    let y = min[1] as number;

    return [
      {
        vertexA: [min[0], y, min[2]],
        vertexB: [max[0], y, min[2]],
        vertexC: [min[0], y, max[2]],
      },
      {
        vertexA: [max[0], y, min[2]],
        vertexB: [max[0], y, max[2]],
        vertexC: [min[0], y, max[2]],
      },
    ];
  }

  private generateBox(): FaceLike[] {
    let min = this.min;
    let max = this.max;

    let v000 = [min[0], min[1], min[2]];
    let v100 = [max[0], min[1], min[2]];
    let v001 = [min[0], min[1], max[2]];
    let v101 = [max[0], min[1], max[2]];
    let v010 = [min[0], max[1], min[2]];
    let v110 = [max[0], max[1], min[2]];
    let v011 = [min[0], max[1], max[2]];
    let v111 = [max[0], max[1], max[2]];

    return [
      { vertexA: v000, vertexB: v100, vertexC: v001 },
      { vertexA: v100, vertexB: v101, vertexC: v001 },
      { vertexA: v010, vertexB: v110, vertexC: v011 },
      { vertexA: v110, vertexB: v111, vertexC: v011 },
      { vertexA: v000, vertexB: v100, vertexC: v010 },
      { vertexA: v100, vertexB: v110, vertexC: v010 },
      { vertexA: v001, vertexB: v101, vertexC: v011 },
      { vertexA: v101, vertexB: v111, vertexC: v011 },
      { vertexA: v000, vertexB: v001, vertexC: v010 },
      { vertexA: v001, vertexB: v011, vertexC: v010 },
      { vertexA: v100, vertexB: v101, vertexC: v110 },
      { vertexA: v101, vertexB: v111, vertexC: v110 },
    ];
  }
}

type FaceLike = {
  vertexA: vec3 | number[];
  vertexB: vec3 | number[];
  vertexC: vec3 | number[];
};
